import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { IconCheck, IconX } from "@/components/Icons";
import {
  Button,
  Card,
  Cell,
  Kbd,
  PageHead,
  Pill,
  Section,
  Spacer,
  Stack,
  Table,
  Th,
  Toolbar,
} from "@/components/kit";
import { importCsvAction, type ImportResult } from "@/lib/actions/mastersImport";
import type { CsvIssue, MasterSummary } from "@/lib/api/types";

/**
 * Getting a season's worth of records in.
 *
 * The design asked for the good rows to wait while the bad ones are fixed, and
 * that is what this does — but not by importing half a file. Writing is still
 * all-or-nothing, because re-uploading is an upsert by code, so a rejected file
 * costs nothing and the state after an import is always "all of it" or "none of
 * it".
 *
 * What was actually painful about all-or-nothing was leaving the app to fix
 * twelve cells in a spreadsheet. So the file is editable here: the rows the
 * server objected to keep their place, marked, and are corrected where they
 * are. Checking again is a keystroke, and Apply only lights up when nothing is
 * outstanding.
 */

/** A tolerable CSV reader: quoted fields, doubled quotes inside them, commas
 * and newlines within quotes. Not a spec-complete parser — the files here come
 * from our own template or a spreadsheet's export, and a wrong guess shows up
 * immediately in a grid the operator is looking at. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (quoted) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          value += '"';
          i++;
        } else quoted = false;
      } else value += char;
      continue;
    }
    if (char === '"') quoted = true;
    else if (char === ",") {
      row.push(value);
      value = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[i + 1] === "\n") i++;
      row.push(value);
      rows.push(row);
      row = [];
      value = "";
    } else value += char;
  }
  if (value !== "" || row.length > 0) {
    row.push(value);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
}

function toCsv(header: string[], rows: string[][]): string {
  const escape = (cell: string) =>
    /[",\n\r]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
  return [header, ...rows].map((r) => r.map(escape).join(",")).join("\n");
}

export function Import({
  sport,
  master,
  template,
}: {
  sport: string;
  master: MasterSummary;
  template: string;
}) {
  const navigate = useNavigate();
  const [header, setHeader] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState<string | null>(null);
  const paste = useRef<HTMLTextAreaElement>(null);

  const report = result?.ok ? result.report : null;
  const issues = report?.issues ?? [];
  const outstanding = issues.length;
  // Checked against what is on screen right now: editing a cell makes the last
  // report stale, and an Apply button lit by a stale report is a trap.
  const [checkedText, setCheckedText] = useState<string | null>(null);
  const current = rows.length > 0 ? toCsv(header, rows) : null;
  const fresh = checkedText !== null && checkedText === current;
  const clean = report !== null && outstanding === 0 && fresh;

  /** Issues indexed by the row number the server used (header is row 1).
   *
   * Keyed on the report rather than on `issues`, which is a fresh array every
   * render and would make this memo do nothing at all. */
  const byRow = useMemo(() => {
    const map = new Map<number, CsvIssue[]>();
    for (const issue of report?.issues ?? []) {
      map.set(issue.row, [...(map.get(issue.row) ?? []), issue]);
    }
    return map;
  }, [report]);

  function load(text: string, fileName: string | null) {
    const parsed = parseCsv(text);
    if (parsed.length === 0) return;
    setHeader(parsed[0]);
    setRows(parsed.slice(1));
    setName(fileName);
    setResult(null);
    setCheckedText(null);
  }

  async function check(commit: boolean) {
    const text = toCsv(header, rows);
    setBusy(true);
    try {
      const answer = await importCsvAction(sport, master.code, text, commit);
      setResult(answer);
      setCheckedText(text);
      if (commit && answer.ok && answer.report.committed) {
        navigate(`/${sport}/masters/${master.code}`);
      }
    } finally {
      // Release the button even if the action rejects at the transport layer.
      setBusy(false);
    }
  }

  const templateHref = `data:text/csv;charset=utf-8,${encodeURIComponent(template)}`;

  return (
    <>
      <PageHead
        title={`Import ${master.label.toLowerCase()}`}
        note="Nothing is written until you apply, and then it is the whole file or none of it."
      />

      <Section
        title="Get the sheet"
        note="Its columns are this master's own, in the order they were declared, with one example row filled in."
      >
        <Stack>
        <Toolbar>
          <Button>
            <a
              href={templateHref}
              download={`${master.code}-template.csv`}
              style={{ color: "inherit", textDecoration: "none" }}
            >
              Download {master.code}-template.csv
            </a>
          </Button>
          <Spacer />
        </Toolbar>
        <Card>
          <pre
            style={{
              margin: 0,
              overflowX: "auto",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: "var(--t-sm)",
              color: "var(--ink-2)",
            }}
          >
            {template}
          </pre>
        </Card>
        </Stack>
      </Section>

      <Section
        title="Bring it back"
        note="Upload the file, or paste straight out of a spreadsheet."
      >
        <Stack>
        <Toolbar>
          <input
            type="file"
            accept=".csv,text/csv"
            style={{ fontSize: "var(--t-sm)" }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) load(await file.text(), file.name);
            }}
          />
          {name && <Pill>{name}</Pill>}
        </Toolbar>
        <textarea
          ref={paste}
          placeholder="…or paste rows here, including the header"
          rows={3}
          style={{
            width: "100%",
            padding: "var(--s2) var(--s3)",
            border: "1px solid var(--line-2)",
            borderRadius: "var(--r)",
            fontSize: "var(--t-sm)",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (text.includes(",") || text.includes("\t")) {
              e.preventDefault();
              // A spreadsheet copies tab-separated; our own template is commas.
              load(text.includes("\t") ? text.replace(/\t/g, ",") : text, "pasted");
              if (paste.current) paste.current.value = "";
            }
          }}
        />
        </Stack>
      </Section>

      {rows.length > 0 && (
        <Section
          title="Fix anything wrong, then apply"
          note="Rows the server objected to keep their place and are marked. Correct them here — there is no need to go back to the spreadsheet."
          actions={
            <div style={{ display: "flex", alignItems: "center", gap: "var(--s3)" }}>
              {report && fresh && outstanding === 0 && (
                <Pill tone="good" dot>
                  <IconCheck size={12} /> {report.created} new · {report.updated} changed
                </Pill>
              )}
              {report && fresh && outstanding > 0 && (
                <Pill tone="bad" dot>
                  {outstanding} need{outstanding === 1 ? "s" : ""} attention
                </Pill>
              )}
              {report && !fresh && <Pill tone="warn">Edited — check again</Pill>}
              <Button disabled={busy} onClick={() => check(false)}>
                {busy ? "Checking…" : "Check"}
              </Button>
              <Button tone="primary" disabled={!clean || busy} onClick={() => check(true)}>
                Apply {rows.length} row{rows.length === 1 ? "" : "s"}
              </Button>
            </div>
          }
        >
          <Stack>
          {result?.ok === false && (
            <Card>
              <p style={{ margin: 0, color: "var(--bad)", fontSize: "var(--t-sm)" }}>
                {result.error}
              </p>
            </Card>
          )}

          <Table>
            <thead>
              <tr>
                <Th>Row</Th>
                {header.map((column) => (
                  <Th key={column}>{column}</Th>
                ))}
                <Th>Problem</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                // The server counts the header as row 1, so the first data row
                // is row 2. Reporting our index instead would send someone to
                // the wrong line of their own file.
                const number = i + 2;
                const problems = byRow.get(number) ?? [];
                return (
                  <tr key={i}>
                    <td style={{ color: "var(--ink-3)", fontSize: "var(--t-xs)" }}>{number}</td>
                    {header.map((column, c) => {
                      const bad = problems.some((p) => p.column === column);
                      return (
                        <Cell
                          key={column}
                          bad={bad}
                          value={row[c] ?? ""}
                          title={problems.find((p) => p.column === column)?.message}
                          onChange={(e) =>
                            setRows(
                              rows.map((r, j) =>
                                j === i
                                  ? Object.assign([...r], { [c]: e.target.value })
                                  : r,
                              ),
                            )
                          }
                        />
                      );
                    })}
                    <td style={{ fontSize: "var(--t-xs)", color: "var(--bad)" }}>
                      {problems.map((p) => p.message).join("; ")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <Toolbar>
            <Button
              tone="subtle"
              onClick={() => {
                setRows([]);
                setHeader([]);
                setResult(null);
                setName(null);
              }}
            >
              <IconX size={15} />
              Start over
            </Button>
            <Spacer />
            <span style={{ fontSize: "var(--t-xs)", color: "var(--ink-3)" }}>
              <Kbd>Tab</Kbd> moves across, <Kbd>↵</Kbd> down
            </span>
          </Toolbar>
          </Stack>
        </Section>
      )}
    </>
  );
}
